import { FC, useEffect, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { PageLink, PageTitle } from '../../../_metronic/layout/core'
import { getPlantsAllSimple } from '../../services/plantsService'
import {
  GenerationAnalysis,
  GenerationAnalysisMonth,
  GenerationAnalysisType,
  getGenerationAnalysis,
} from '../../services/generationAnalysisService'
import { SelectGlobal } from '../components/SelectGlobal'
import { LoadingSpan } from '../components/Loadings/loadingSpan'
import { useToast } from '../components/Toats'
import listLayoutStyles from '../components/ListTableLayout/listTableLayout.module.css'
import { GenerationAnalysisChart } from './components/GenerationAnalysisChart'
import {
  PeriodRange,
  PeriodRangePicker,
  defaultPeriod,
  formatMonthKey,
  formatPeriod,
} from './components/PeriodRangePicker'
import { formatEnergy, formatKwh, formatPercent, performanceColor } from './utils'
import styles from './generationAnalysis.module.css'

const breadcrumbs: Array<PageLink> = [
  { title: 'Início', path: '/dashboard', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
  { title: 'Relatórios', path: '/relatorios', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
]

const MAX_ITEMS: Record<GenerationAnalysisType, number> = { monthly: 12, annual: 5 }

// A API devolve start/end sempre como "YYYY-MM"; no modo anual o rótulo usa só o ano
const analysisPeriodLabel = (analysis: GenerationAnalysis) =>
  analysis.type === 'annual'
    ? formatPeriod('annual', { start: analysis.start.substring(0, 4), end: analysis.end.substring(0, 4) })
    : formatPeriod('monthly', { start: analysis.start, end: analysis.end })

type KpiProps = {
  icon: string
  color: string
  title: string
  value: string
  hint?: string
  children?: React.ReactNode
}

function KpiCard({ icon, color, title, value, hint, children }: KpiProps) {
  return (
    <div className={styles.kpiCard}>
      <span className={`${styles.kpiAccent} bg-${color}`} />
      <div className='d-flex align-items-center gap-3'>
        <span className={`${styles.kpiIcon} bg-light-${color} text-${color}`}>
          <i className={`bi ${icon} text-${color}`} />
        </span>
        <span className='fw-semibold text-gray-600 fs-7'>{title}</span>
      </div>
      <div className={styles.kpiValue}>{value}</div>
      {hint && <div className={styles.kpiHint}>{hint}</div>}
      {children}
    </div>
  )
}

function SourceBadges({ month }: { month: GenerationAnalysisMonth }) {
  if (!month.hasGenerationReport && !month.hasDetailedReport) {
    return <span className='badge badge-light-secondary text-gray-600'>Sem relatório</span>
  }
  return (
    <div className='d-flex flex-wrap gap-1 justify-content-end'>
      {month.hasGenerationReport && <span className='badge badge-light-primary'>Geração</span>}
      {month.hasDetailedReport && <span className='badge badge-light-warning'>Detalhado</span>}
    </div>
  )
}

function Summary({ analysis }: { analysis: GenerationAnalysis }) {
  const { totals } = analysis
  const balancePositive = (totals.balance ?? 0) >= 0
  const performance = totals.performance

  return (
    <div className={`${styles.kpiGrid} mb-6`}>
      <KpiCard
        icon='bi-lightning-charge-fill'
        color='primary'
        title='Geração total'
        value={formatEnergy(totals.generation)}
        hint={`Energia produzida em ${analysisPeriodLabel(analysis)}`}
      />
      <KpiCard
        icon='bi-graph-up-arrow'
        color='danger'
        title='Prognóstico'
        value={formatEnergy(totals.prognosis)}
        hint='Quanto era esperado gerar no período'
      />
      <KpiCard
        icon='bi-speedometer2'
        color={performanceColor(performance)}
        title='Desempenho'
        value={formatPercent(performance)}
        hint='Geração ÷ prognóstico'
      >
        <div className='h-6px w-100 bg-light rounded mt-3'>
          <div
            className={`bg-${performanceColor(performance)} rounded h-6px`}
            style={{ width: `${Math.min(performance ?? 0, 100)}%` }}
          />
        </div>
      </KpiCard>
      <KpiCard
        icon='bi-plug-fill'
        color='warning'
        title='Consumo'
        value={formatEnergy(totals.consumption)}
        hint={totals.consumption === null
          ? 'Nenhum relatório detalhado gerado no período'
          : 'Consumo total dos dias cobertos por relatórios gerados'}
      />
      <KpiCard
        icon={balancePositive ? 'bi-arrow-up-circle-fill' : 'bi-arrow-down-circle-fill'}
        color={totals.balance === null ? 'secondary' : balancePositive ? 'success' : 'danger'}
        title='Saldo (geração − consumo)'
        value={totals.balance === null ? '—' : `${balancePositive ? '+' : ''}${formatEnergy(totals.balance)}`}
        hint={totals.coverage === null
          ? 'Disponível quando houver consumo'
          : `Nos dias com consumo, a geração cobriu ${formatPercent(totals.coverage)} dele`}
      />
    </div>
  )
}

function MonthsTable({ analysis }: { analysis: GenerationAnalysis }) {
  return (
    <div className={styles.tableWrap}>
      <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 mb-0'>
        <thead>
          <tr className='fw-bold text-muted fs-7 text-uppercase'>
            <th className='min-w-100px'>Mês</th>
            <th className='text-end min-w-110px'>Geração</th>
            <th className='text-end min-w-110px'>Prognóstico</th>
            <th className='min-w-150px ps-6'>Desempenho</th>
            <th className='text-end min-w-110px'>Consumo</th>
            <th className='text-end min-w-110px'>Saldo</th>
            <th className='text-end min-w-140px'>Relatórios</th>
          </tr>
        </thead>
        <tbody>
          {analysis.months.map((month) => {
            const balance = month.balance
            const color = performanceColor(month.performance)
            return (
              <tr key={month.month}>
                <td className='fw-bold text-gray-800'>{formatMonthKey(month.month)}</td>
                <td className='text-end text-gray-800 fw-semibold'>
                  {month.generationSource === 'none' ? <span className='text-muted'>—</span> : formatKwh(month.generation)}
                  {month.generationSource === 'charts' && (
                    <i className='bi bi-info-circle text-muted ms-1' title='Sem relatório no mês: valor somado dos gráficos diários' />
                  )}
                </td>
                <td className='text-end text-gray-600'>{formatKwh(month.prognosis)}</td>
                <td className='ps-6'>
                  <div className='d-flex align-items-center gap-2'>
                    <div className='h-6px w-100 bg-light rounded' style={{ maxWidth: '6rem' }}>
                      <div className={`bg-${color} rounded h-6px`} style={{ width: `${Math.min(month.performance ?? 0, 100)}%` }} />
                    </div>
                    <span className={`fw-bold fs-7 text-${color}`}>{formatPercent(month.performance)}</span>
                  </div>
                </td>
                <td className='text-end text-gray-600'>
                  {month.consumption === null ? <span className='text-muted'>—</span> : formatKwh(month.consumption)}
                  {month.consumption !== null && month.consumptionCoveredDays < month.daysInMonth && (
                    <div className='fs-8 text-warning' title='Dias do mês cobertos pelo período de leitura de relatórios detalhados gerados'>
                      {month.consumptionCoveredDays} de {month.daysInMonth} dias
                    </div>
                  )}
                </td>
                <td className={`text-end fw-semibold ${balance === null ? 'text-muted' : balance >= 0 ? 'text-success' : 'text-danger'}`}>
                  {balance === null ? '—' : `${balance >= 0 ? '+' : ''}${formatKwh(balance)}`}
                </td>
                <td className='text-end'>
                  <SourceBadges month={month} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function HowToRead({ isDaily }: { isDaily: boolean }) {
  return (
    <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-5 mt-6'>
      <i className='bi bi-lightbulb-fill text-primary fs-2 me-4' />
      <div className='fs-7 text-gray-700'>
        <div className='fw-bold text-gray-900 fs-6 mb-2'>Como os valores são calculados</div>
        <ul className='mb-0 ps-4'>
          <li>
            <b>Geração:</b> total do mês salvo no relatório de geração; na falta dele, o do relatório detalhado.
            {isDaily && ' As barras diárias vêm do histórico de geração da usina.'}
          </li>
          <li>
            <b>Prognóstico:</b> valor esperado do mês informado no cadastro de prognóstico da usina.
            {isDaily && ' No gráfico diário ele é dividido igualmente entre os dias do mês.'}
          </li>
          <li>
            <b>Consumo:</b> consumo total dos relatórios detalhados gerados (energia registrada nas faturas + consumo instantâneo).
            Como cada fatura cobre um período de leitura (ex.: 28/01 a 12/02), esse total é dividido pelos dias do período
            e cada mês soma apenas os dias que algum relatório cobre, inclusive relatórios de meses vizinhos.
            Dias sem relatório gerado ficam sem consumo.
            {isDaily && ' No gráfico diário, cada dia mostra a média diária do período de leitura que o cobre.'}
          </li>
          <li>
            <b>Saldo:</b> geração − consumo considerando apenas os dias cobertos pelo consumo.
          </li>
          <li>
            <b>Desempenho:</b> geração ÷ prognóstico. Verde a partir de 95%, amarelo entre 80% e 95%, vermelho abaixo de 80%.
          </li>
        </ul>
      </div>
    </div>
  )
}

const GenerationAnalysisPage: FC = () => {
  const { toggleToast } = useToast()
  const [plant, setPlant] = useState<string>('')
  const [type, setType] = useState<GenerationAnalysisType>('monthly')
  const [period, setPeriod] = useState<PeriodRange>(defaultPeriod('monthly'))

  const { data: plants = [], isLoading: loadingPlants } = useQuery({
    queryKey: ['getPlantsGenerationAnalysis'],
    queryFn: async () => {
      try {
        return await getPlantsAllSimple()
      } catch (error) {
        toggleToast('Erro ao buscar a lista de usinas. ', 'error')
        throw error
      }
    },
    staleTime: 1000 * 60 * 60,
  })

  // Seleciona a primeira usina para a tela já abrir com um gráfico
  useEffect(() => {
    if (!plant && plants.length > 0) setPlant(String(plants[0].id))
  }, [plants, plant])

  const { start, end } = period
  const { data: analysis, isFetching, isError, error } = useQuery({
    queryKey: ['getGenerationAnalysis', plant, type, start, end],
    queryFn: () => getGenerationAnalysis(plant, type, start, end),
    enabled: !!plant,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
  })

  const changeType = (newType: GenerationAnalysisType) => {
    if (newType === type) return
    setType(newType)
    setPeriod(defaultPeriod(newType))
  }

  const errorMessage = (error as any)?.response?.data?.message || 'Não foi possível carregar a análise.'
  const isDaily = analysis?.granularity === 'day'
  const missingDaily = isDaily && analysis?.months.some((month) => !month.dailyAvailable)

  return (
    <div className='d-flex flex-column gap-6'>
      {/* Filtros */}
      <div className={`card ${listLayoutStyles.listCard}`}>
        <div className='card-body py-6'>
          <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-5'>
            <div>
              {/* <h3 className='fw-bold text-gray-900 mb-1'>Análise de geração</h3>
              <span className='text-muted fs-7'>
                Compare a energia gerada pela usina com o prognóstico e com o consumo das unidades, a partir dos relatórios gerados.
              </span> */}
            </div>
            {isFetching && (
              <span className='text-muted fs-7 d-flex align-items-center'>
                Atualizando <LoadingSpan />
              </span>
            )}
          </div>

          <div className={`${styles.filtersPanel} ${styles.filtersGrid}`}>
            <div>
              <div className={styles.filterLabel}><span className={styles.filterStep}>1</span>Usina</div>
              <SelectGlobal
                itens={plants}
                buttonAll={false}
                textSearch='Buscar usina'
                textButtonInit='Selecione uma usina'
                labelKey='name'
                valueKey='id'
                typeIcon='img'
                linkIcon='https://res.cloudinary.com/deq3ehxe3/image/upload/v1735238752/plant2_tuwshh.png'
                value={plant}
                loading={loadingPlants}
                className={styles.selectFull}
                onChange={(value: string) => setPlant(value)}
              />
            </div>

            <div>
              <div className={styles.filterLabel}><span className={styles.filterStep}>2</span>Visualizar por</div>
              <div className={styles.typeToggle}>
                <button type='button' className={type === 'monthly' ? styles.typeActive : ''} onClick={() => changeType('monthly')}>
                  <i className='bi bi-calendar3 me-2' />Mensal <span className='fw-normal fs-8 text-muted'>(dias)</span>
                </button>
                <button type='button' className={type === 'annual' ? styles.typeActive : ''} onClick={() => changeType('annual')}>
                  <i className='bi bi-calendar-range me-2' />Anual <span className='fw-normal fs-8 text-muted'>(meses)</span>
                </button>
              </div>
            </div>

            <div>
              <div className={styles.filterLabel}>
                <span className={styles.filterStep}>3</span>
                {type === 'annual' ? 'Ano(s)' : 'Mês(es)'}
                <i
                  className='bi bi-question-circle text-muted'
                  title={`Selecione um intervalo de 1 a ${MAX_ITEMS[type]} ${type === 'annual' ? 'anos' : 'meses'}`}
                />
              </div>
              <PeriodRangePicker type={type} value={period} onChange={setPeriod} maxItems={MAX_ITEMS[type]} />
            </div>
          </div>
        </div>
      </div>

      {!plant && !loadingPlants && (
        <div className='card'>
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}><i className='bi bi-sun text-primary fs-1' /></span>
            <h4 className='fw-bold text-gray-800 mb-0'>Selecione uma usina</h4>
            <span className='text-muted'>Escolha a usina, o tipo de visualização e o período para ver a análise.</span>
          </div>
        </div>
      )}

      {plant && isError && !analysis && (
        <div className='card'>
          <div className={styles.emptyState}>
            <span className={`${styles.emptyIcon} bg-light-danger`}><i className='bi bi-exclamation-triangle text-danger fs-1' /></span>
            <h4 className='fw-bold text-gray-800 mb-0'>Erro ao carregar</h4>
            <span className='text-muted'>{errorMessage}</span>
          </div>
        </div>
      )}

      {plant && !analysis && isFetching && (
        <div className='card'>
          <div className={styles.emptyState}>
            <span className='spinner-border text-primary' role='status' />
            <span className='text-muted'>Calculando geração, prognóstico e consumo...</span>
          </div>
        </div>
      )}

      {analysis && (
        <div style={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
          <Summary analysis={analysis} />

          {/* Gráfico */}
          <div className='card mb-6'>
            <div className='card-header border-0 pt-6 align-items-start'>
              <div className='card-title flex-column align-items-start'>
                <span className='fw-bold text-gray-900 fs-3'>
                  {isDaily ? 'Geração diária' : 'Geração mensal'} · {analysis.plant.name}
                </span>
                <span className='text-muted fw-semibold fs-7 mt-1'>
                  {analysisPeriodLabel(analysis)}
                </span>
              </div>
            </div>
            <div className='card-body pt-2'>
              {missingDaily && (
                <div className='alert alert-light-warning d-flex align-items-center py-3 fs-7 mb-4'>
                  <i className='bi bi-exclamation-circle text-warning fs-4 me-3' />
                  Alguns meses não possuem histórico diário de geração disponível; os totais do mês continuam considerando os relatórios.
                </div>
              )}
              <GenerationAnalysisChart analysis={analysis} />
            </div>
          </div>

          {/* Detalhamento mensal */}
          <div className='card'>
            <div className='card-header border-0 pt-6'>
              <div className='card-title flex-column align-items-start'>
                <span className='fw-bold text-gray-900 fs-3'>Resumo por mês</span>
                <span className='text-muted fw-semibold fs-7 mt-1'>Valores consolidados dos relatórios de geração e detalhado</span>
              </div>
            </div>
            <div className='card-body pt-2'>
              <MonthsTable analysis={analysis} />
              <HowToRead isDaily={!!isDaily} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const GenerationAnalysisWrapper: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>Análises</PageTitle>
      <GenerationAnalysisPage />
    </>
  )
}

export { GenerationAnalysisWrapper }
